import React from 'react';
import { Button } from '@mui/material';
import { ReactComponent as SeatIcon } from '../../assets/images/floor/mosqueIcon.svg';

/**
 * Seat Component
 * Displays a single seat with visual feedback for availability
 */
function Seat({ name, available = true, onClick }) {
	return (
		<div
			className={`seat-icon create ${!available ? 'reserved' : ''}`}
			onClick={onClick}
			style={{ cursor: onClick ? 'pointer' : 'default' }}
		>
			<SeatIcon />
			<p className='seat-icon-info'>{name}</p>
		</div>
	);
}

/**
 * Row Component
 * Displays a row of seats with add/remove controls
 */
function Row({ row, onDeleteColumn, onModifyRow, disabled = false }) {
	function toggleSeatAvailability(index) {
		if (disabled) return;

		const modifiedRow = { ...row };
		modifiedRow.seats = modifiedRow.seats.map((seat, j) =>
			j === index
				? {
					...seat,
					isReservedForSpecialGuest: !seat.isReservedForSpecialGuest,
					isReserved: !seat.isReserved
				}
				: seat
		);
		onModifyRow(modifiedRow);
	}

	function handleDeleteColumn() {
		if (disabled || row.seats.length === 0) return;
		onDeleteColumn(row.seats.length - 1);
	}

	function handleAddSeat() {
		if (disabled) return;

		const modifiedRow = {
			...row,
			seats: [
				...row.seats,
				{
					number: String(row.seats.length + 1),
					isReservedForSpecialGuest: false,
					isReserved: false,
					notes: ''
				}
			],
		};
		onModifyRow(modifiedRow);
	}

	return (
		<div
			className='seat-view-wrapper'
			style={{
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				margin: '0 auto',
			}}
		>
			<div className='wrap' style={{ display: 'flex', alignItems: 'center' }}>
				{row.seats.map((seat, index) => (
					<Seat
						key={seat.number}
						name={seat.number}
						available={!seat.isReservedForSpecialGuest && !seat.isReserved}
						onClick={disabled ? undefined : () => toggleSeatAvailability(index)}
					/>
				))}
			</div>
			<div
				className='seat-buttons'
				style={{
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'center',
					marginLeft: '1rem',
					gap: '20px',
				}}
			>
				<Button
					variant='contained'
					color='error'
					className='remove-seat'
					onClick={handleDeleteColumn}
					disabled={disabled}
				>
					<i className='fa-solid fa-minus'></i>
				</Button>
				<Button
					variant='contained'
					color='primary'
					className='add-seat'
					onClick={handleAddSeat}
					disabled={disabled}
				>
					<i className='fa-solid fa-plus'></i>
				</Button>
			</div>
		</div>
	);
}

/**
 * Floor Component
 * Displays a complete floor with all rows and controls
 */
function Floor({ floorName, floorCode, rows, onModifyRow, disabled = false }) {
	function handleDeleteColumn(rowIndex, columnIndex) {
		if (disabled) return;

		const updatedRows = rows.map((row, i) => {
			if (i === rowIndex) {
				const newSeats = row.seats.filter((seat, j) => j !== columnIndex);
				return { ...row, seats: newSeats };
			}
			return row;
		});
		onModifyRow(recalcSeatNumbers(updatedRows));
	}

	function handleModifyRow(rowIndex, modifiedRow) {
		if (disabled) return;

		const updatedRows = rows.map((row, i) =>
			i === rowIndex ? modifiedRow : row
		);
		onModifyRow(recalcSeatNumbers(updatedRows));
	}

	function addRow() {
		if (disabled) return;

		const numColumns = rows.length > 0 ? rows[0].seats.length : 3;

		const newRow = {
			rowName: floorName.charAt(0) + getRowName(rows.length),
			seats: Array.from({ length: numColumns }, (_, index) => ({
				number: String(index + 1),
				isReservedForSpecialGuest: false,
				isReserved: false,
				notes: ''
			})),
		};

		const updatedRows = recalcSeatNumbers([...rows, newRow]);
		onModifyRow(updatedRows);
	}

	function getRowName(index) {
		const baseChar = 65; // ASCII code for 'A'
		let rowName = '';

		while (index >= 0) {
			const charCode = baseChar + (index % 26);
			rowName = String.fromCharCode(charCode) + rowName;
			index = Math.floor(index / 26) - 1;
		}
		return rowName;
	}

	function recalcSeatNumbers(rows) {
		return rows.map((row) => ({
			...row,
			seats: row.seats.map((seat, index) => ({
				...seat,
				number: String(index + 1),
			})),
		}));
	}

	return (
		<div className='floor'>
			<h2>{floorName} {floorCode && `(${floorCode})`}</h2>
			<div
				className='floor-wrap'
				style={{
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
				}}
			>
				{rows.map((row, rowIndex) => (
					<Row
						key={row.rowName}
						row={row}
						rowIndex={rowIndex}
						onDeleteColumn={(columnIndex) =>
							handleDeleteColumn(rowIndex, columnIndex)
						}
						onModifyRow={(modifiedRow) =>
							handleModifyRow(rowIndex, modifiedRow)
						}
						disabled={disabled}
					/>
				))}
			</div>
			<Button
				variant='outlined'
				onClick={addRow}
				disabled={disabled}
				style={{ marginTop: '1rem' }}
			>
				Add Row
			</Button>
		</div>
	);
}

/**
 * FloorPlanBuilder Component
 * Reusable floor plan builder with visual seat editor
 * 
 * @param {Array} floors - Array of floor objects
 * @param {Function} onModifyFloor - Callback when a floor is modified
 * @param {Boolean} disabled - Whether editing is disabled
 */
export default function FloorPlanBuilder({ floors = [], onModifyFloor, disabled = false }) {
	return (
		<div className='floor-plan-builder'>
			{floors.map((floor, index) => (
				<Floor
					key={index}
					floorName={floor.floorName}
					floorCode={floor.floorCode}
					rows={floor.rows}
					onModifyRow={(updatedRows) => onModifyFloor(index, updatedRows)}
					disabled={disabled}
				/>
			))}
		</div>
	);
}
